import { useUpdateAnalysis } from '@modules/analysis/hooks/useUpdateAnalysis';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { getAnalysisRoute } from '@modules/bookmark/helpers';
import { ArtefactRename } from '@modules/common/components/ArtefactRename';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useGetQuantifyQueryMetadata } from '@modules/quantify/hooks';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';

export const useRenameQuantify = () => {
  const {
    id,
    title,
    scope,
    viewToken,
    editToken,
    createdBy,
    onRenameAnalysis,
    useCase,
  } = useAnalysisStore(
    (analysis) => ({
      id: analysis.id,
      title: analysis.title,
      scope: analysis.scope,
      viewToken: analysis.viewToken,
      editToken: analysis.editToken,
      createdBy: analysis.createdBy,
      onRenameAnalysis: analysis.onRenameAnalysis,
      useCase: analysis.useCase,
    }),
    shallow,
  );

  const {
    tenant: { id: tenantID, slug },
  } = useGetUser();

  const navigate = useNavigate();
  const location = useLocation();

  const queryMetadata = useGetQuantifyQueryMetadata();

  const { openModal, closeModal } = useMuiModalContext();
  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const { updateAnalysis, error } = useUpdateAnalysis({
    name: title,
    scope: mapFromEdgeScope(scope),
    id: id,
    editToken: editToken,
    createdBy: createdBy,
    metadata: queryMetadata,
  });

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: `Failed to update Quantify.`,
        props: { isClosable: true },
      });
    }
  }, [error, showErrorNotification]);

  const onRename = useCallback(
    async (name: string) => {
      try {
        if (editToken) {
          await updateAnalysis({
            variables: {
              title: name,
              scope,
              id,
              editToken,
              createdBy: createdBy?.id,
              tenantID,
              metadata: queryMetadata,
            },
          });
          onRenameAnalysis(name, slug);
          navigate(
            getAnalysisRoute({
              viewToken,
              title: name,
              useCase,
            }),
            { state: location.state, replace: true },
          );
          closeModal();
          showSuccessNotification({
            contentComponent: `Renamed the Quantify.`,
            props: { isClosable: true },
          });
        }
      } catch {
        showErrorNotification({
          contentComponent: `Failed to update Quantify.`,
          props: { isClosable: true },
        });
      }
    },
    [
      editToken,
      updateAnalysis,
      scope,
      id,
      createdBy?.id,
      tenantID,
      queryMetadata,
      onRenameAnalysis,
      slug,
      navigate,
      location,
      viewToken,
      useCase,
      closeModal,
      showSuccessNotification,
      showErrorNotification,
    ],
  );

  return useCallback(() => {
    openModal({
      contentComponent: () => (
        <ArtefactRename
          artefactType="quantify"
          initialValue={title}
          onSubmit={onRename}
          onClose={closeModal}
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'sm',
        PaperProps: {
          elevation: 3,
        },
      },
    });
  }, [closeModal, title, onRename, openModal]);
};
